// @flow

import * as React from 'react';

import hoistNonReactStatics from 'hoist-non-react-statics';

import { createSelector } from 'reselect';

import { selectPricingInfos } from '@gandi/billing';
import { useResourcePost } from '@gandi/resource/react';

import type { FetchParams } from '@gandi/resource/redux';

import { getDataDependency, getDisplayName } from 'gandi.react-helpers';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { quotationsResource } from '../../modules/cloud/resources/quotations';
import type { FullState } from '../../types';

export type FetchQuotationsParams = {
  country: string,
  currency: string,
  grid: string,
  sharing_id: string,
  with_contract?: boolean,
};

export const defaultMode = {
  duration_unit: 'd',
  duration: 30,
};

export const selectFetchQuotationsParams: (
  state: FullState,
) => FetchQuotationsParams =
  // $FlowFixMe[incompatible-call]
  createSelector([selectPricingInfos], pricingInfos => ({
    country: pricingInfos.country,
    currency: pricingInfos.currency,
    duration_unit: 'd',
    duration: 30,
    grid: pricingInfos.grid,
    sharing_id: pricingInfos.sharingId,
    with_contract: true,
  }));

export function useQuotations(): {
  fetchQuotationsParams: FetchQuotationsParams,
  handlePostQuotations: any,
  isPostingQuotations: boolean,
} {
  const fetchQuotationsParams = useAppSelector(selectFetchQuotationsParams);
  const { handlePost: handlePostQuotations, isPosting: isPostingQuotations } =
    useResourcePost(
      quotationsResource,
      // $FlowFixMe[incompatible-cast]
      (fetchQuotationsParams: FetchParams),
    );

  return {
    fetchQuotationsParams,
    handlePostQuotations,
    isPostingQuotations,
  };
}

export function withQuotations<C: React.ComponentType<any>>(
  WrappedComponent: C,
): any {
  function WithQuotations(props: React.ElementConfig<C>) {
    const quotationsProps = useQuotations();

    return <WrappedComponent {...props} {...quotationsProps} />;
  }

  const HoistedComponent = hoistNonReactStatics(
    WithQuotations,
    WrappedComponent,
  );

  HoistedComponent.WrappedComponent = WrappedComponent;
  HoistedComponent.displayName = `WithQuotations(${getDisplayName(
    WrappedComponent,
  )})`;
  HoistedComponent.preFetch = getDataDependency(WrappedComponent, 'preFetch');

  return HoistedComponent;
}
